import React, {useState, useEffect, useContext} from "react";
import qs from 'query-string'

import * as Yup from "yup";
import {useHistory} from "react-router-dom";
import {Link as RouterLink} from "react-router-dom";
import {toast} from "react-toastify";
import {Formik, Form, Field} from "formik";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import CircularProgress from "@material-ui/core/CircularProgress";
import {Helmet} from "react-helmet";
import Card from "@material-ui/core/Card";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckIcon from "@material-ui/icons/Check";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";

import usePlans from '../../hooks/usePlans';
import {i18n} from "../../translate/i18n";
import {openApi} from "../../services/api";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import ColorModeContext from "../../layout/themeContext";
import {getBackendUrl} from "../../config";

const useStyles = makeStyles(theme => {
    const {mode} = theme;
    return ({
        root: {
            width: "100vw",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            boxSizing: "border-box",
            position: "relative",
            backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
            backgroundImage: "none",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center center",
        },
        dialogPaper: {
            borderRadius: "16px",
            maxWidth: "900px",
            width: "100%",
            maxHeight: "90vh",
            margin: "20px",
            overflow: "hidden",
        },
        dialogContent: {
            padding: "0",
            overflowY: "auto",
            overflowX: "hidden",
            maxHeight: "calc(90vh - 64px)",
        },
        container: {
            padding: "32px",
            [theme.breakpoints.down("sm")]: {
                padding: "24px",
            },
        },
        header: {
            textAlign: "center",
            marginBottom: "24px",
        },
        title: {
            fontSize: "24px",
            fontWeight: 700,
            marginBottom: "8px",
            color: theme.palette.primary.main,
            [theme.breakpoints.down("sm")]: {
                fontSize: "20px",
            },
        },
        subtitle: {
            fontSize: "14px",
            color: theme.palette.text.secondary,
            marginBottom: "24px",
        },
        stepsContainer: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "32px",
            position: "relative",
        },
        stepLine: {
            position: "absolute",
            top: "16px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "250px",
            height: "2px",
            backgroundColor: theme.palette.divider,
            zIndex: 0,
            [theme.breakpoints.down("sm")]: {
                width: "150px",
            },
        },
        stepLineActive: {
            backgroundColor: theme.palette.primary.main,
            transition: "width 0.3s ease",
        },
        step: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
        },
        stepCircle: {
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.background.paper,
            border: `2px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            fontWeight: 600,
            fontSize: "14px",
            marginBottom: "6px",
            transition: "all 0.3s ease",
        },
        stepCircleActive: {
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            color: "white",
        },
        stepCircleCompleted: {
            backgroundColor: theme.palette.success.main,
            borderColor: theme.palette.success.main,
            color: "white",
        },
        stepLabel: {
            fontSize: "12px",
            fontWeight: 500,
            color: theme.palette.text.secondary,
        },
        stepLabelActive: {
            color: theme.palette.primary.main,
            fontWeight: 600,
        },
        contentCard: {
            backgroundColor: "transparent",
            padding: "0",
            marginBottom: "0",
        },
        plansGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
            marginBottom: "20px",
            [theme.breakpoints.down("sm")]: {
                gridTemplateColumns: "1fr",
                gap: "12px",
            },
        },
        planCard: {
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            padding: "0",
            transition: "all 0.3s ease",
            cursor: "pointer",
            position: "relative",
            overflow: "hidden",
            "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
            },
        },
        planCardSelected: {
            borderColor: theme.palette.primary.main,
            borderWidth: "2px",
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}15`,
        },
        planHeader: {
            padding: "16px",
            borderBottom: `1px solid ${theme.palette.divider}`,
            textAlign: "center",
        },
        planName: {
            fontSize: "18px",
            fontWeight: 700,
            marginBottom: "8px",
            color: theme.palette.text.primary,
        },
        planPrice: {
            fontSize: "28px",
            fontWeight: 700,
            color: theme.palette.primary.main,
            marginBottom: "2px",
        },
        planPricePeriod: {
            fontSize: "12px",
            color: theme.palette.text.secondary,
        },
        planFeatures: {
            padding: "16px",
            maxHeight: "300px",
            overflowY: "auto",
        },
        featuresGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px 16px",
            [theme.breakpoints.down("xs")]: {
                gridTemplateColumns: "1fr",
            },
        },
        featureItem: {
            display: "flex",
            alignItems: "center",
            fontSize: "13px",
        },
        featureIcon: {
            color: theme.palette.success.main,
            marginRight: "8px",
            fontSize: "16px",
            minWidth: "16px",
            flexShrink: 0,
        },
        planActions: {
            padding: "12px 16px",
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        selectedPlanBar: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            backgroundColor: theme.palette.action.hover,
            borderRadius: "8px",
            marginBottom: "20px",
        },
        backButton: {
            display: "flex",
            alignItems: "center",
            color: theme.palette.text.secondary,
            textDecoration: "none",
            marginBottom: "16px",
            fontSize: "14px",
            "&:hover": {
                color: theme.palette.primary.main,
            },
        },
        form: {
            width: "100%",
            paddingBottom: "20px",
        },
        submit: {
            marginTop: "20px",
            marginBottom: "10px",
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: "white",
            borderRadius: "10px",
            padding: "12px 0",
            fontSize: "15px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: `0 3px 10px ${theme.palette.primary.main}40`,
            "&:hover": {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
                transform: "translateY(-1px)",
                boxShadow: `0 4px 16px ${theme.palette.primary.main}66`,
            },
        },
        avatar: {
            margin: "0 auto 12px",
            width: "48px",
            height: "48px",
            backgroundColor: theme.palette.primary.main,
        },
        "@keyframes slideIn": {
            from: {
                opacity: 0,
                transform: "translateY(20px)",
            },
            to: {
                opacity: 1,
                transform: "translateY(0)",
            },
        },
        slideIn: {
            animation: "$slideIn 0.4s ease-out",
        },
    })
});

const UserSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Muito curto!")
        .max(50, "Muito longo!")
        .required("Obrigatório"),
    companyName: Yup.string()
        .min(2, "Muito curto!")
        .max(50, "Muito longo!")
        .required("Obrigatório"),
    document: Yup.string()
        .test('document-format', 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', function (value) {
            if (!value) return false;
            const numbers = value.replace(/\D/g, '');
            return numbers.length === 11 || numbers.length === 14;
        })
        .required("CPF ou CNPJ é obrigatório"),
    password: Yup.string().min(5, "Mínimo 5 caracteres").max(50, "Máximo 50 caracteres").required("Obrigatório"),
    email: Yup.string().email("Email inválido").required("Obrigatório"),
    phone: Yup.string().required("Obrigatório"),
});

const SignUp = () => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const {colorMode} = useContext(ColorModeContext);
    const {appLogoFavicon, appName, mode} = colorMode || {};
    const currentMode = mode || theme.palette?.type || "light";
    const {getPlanList} = usePlans()
    const [plans, setPlans] = useState([])
    const [loading, setLoading] = useState(false);
    const [validatingCnpj, setValidatingCnpj] = useState(false);
    const [lastValidatedDocument, setLastValidatedDocument] = useState("");
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const {getPublicSetting} = useSettings();

    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const params = qs.parse(window.location.search);
    // Whitelabel (fundo, etc.) é único para todas as companies: sempre null (backend usa company 1)
    const companyId = params.companyId != null ? parseInt(params.companyId, 10) : null;

    const initialState = {name: "", email: "", password: "", phone: "", companyId, companyName: "", document: "", planId: ""};

    useEffect(() => {
        getPublicSetting("userCreation")
            .then((data) => {
                if (data === "disabled") {
                    toast.error(i18n.t("signup.toasts.disabled"));
                    history.push("/login");
                }
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight && String(bgLight).trim()) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + String(bgLight).trim();
                    setBackgroundLight(fullUrl);
                }
            })
            .catch(() => {});

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark && String(bgDark).trim()) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + String(bgDark).trim();
                    setBackgroundDark(fullUrl);
                }
            })
            .catch(() => {});
    }, [history, companyId]);

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                const planList = await getPlanList({listPublic: "false"});
                setPlans(planList.plans || planList || []);
            } catch (error) {
                toast.error("Erro ao carregar planos");
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    const formatCurrency = (value) => {
        if (!value) return "R$ 0,00";
        const numValue = parseFloat(value);
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(numValue);
    };

    const getPlanFeatures = (plan) => {
        const features = [];
        
        if (plan.users) {
            features.push(`${plan.users} Usuários`);
        }
        if (plan.connections) {
            features.push(`${plan.connections} Conexões WhatsApp`);
        }
        if (plan.queues) {
            features.push(`${plan.queues} Filas`);
        }
        if (plan.useWhatsapp) {
            features.push("WhatsApp");
        }
        if (plan.useFacebook) {
            features.push("Facebook");
        }
        if (plan.useInstagram) {
            features.push("Instagram");
        }
        if (plan.useCampaigns) {
            features.push("Campanhas");
        }
        if (plan.useSchedules) {
            features.push("Agendamentos");
        }
        if (plan.useInternalChat) {
            features.push("Chat Interno");
        }
        if (plan.useExternalApi) {
            features.push("API Externa");
        }
        if (plan.useKanban) {
            features.push("Kanban");
        }
        if (plan.useOpenAi) {
            features.push("OpenAI");
        }
        if (plan.useIntegrations) {
            features.push("Integrações");
        }
        if (plan.useWhatsappOfficial) {
            features.push("WhatsApp Oficial");
        }
        if (plan.wavoip) {
            features.push("WAVoIP");
        }
        if (plan.trial && plan.trialDays) {
            features.push(`${plan.trialDays} dias grátis`);
        }
        if (plan.diskQuota) {
            const diskQuotaGB = (plan.diskQuota / (1024 * 1024 * 1024)).toFixed(1);
            features.push(`${diskQuotaGB} GB de armazenamento`);
        }

        return features;
    };

    const handlePlanSelect = (plan) => {
        setSelectedPlan(plan);
        setCurrentStep(2);
    };

    const handleBackToPlans = () => {
        setCurrentStep(1);
    };

    const validateCpf = (cpf) => {
        const cleanCpf = cpf.replace(/\D/g, '');

        if (cleanCpf.length !== 11) return false;

        if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
        }
        let remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(9))) return false;

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
        }
        remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(10))) return false;

        return true;
    };

    const formatCpf = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 11);

        if (limitedNumbers.length <= 3) return limitedNumbers;
        if (limitedNumbers.length <= 6) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3)}`;
        if (limitedNumbers.length <= 9) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6)}`;
        return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6, 9)}-${limitedNumbers.slice(9)}`;
    };

    const formatCnpj = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 14);

        if (limitedNumbers.length <= 2) return limitedNumbers;
        if (limitedNumbers.length <= 5) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2)}`;
        if (limitedNumbers.length <= 8) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5)}`;
        if (limitedNumbers.length <= 12) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8)}`;
        return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}-${limitedNumbers.slice(12)}`;
    };

    const formatDocument = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return formatCpf(value);
        } else {
            return formatCnpj(value);
        }
    };

    const validateDocument = async (document, setFieldValue) => {
        const cleanDocument = document.replace(/\D/g, '');

        if (lastValidatedDocument === cleanDocument) {
            return true;
        }

        const documentType = cleanDocument.length <= 11 ? 'cpf' : 'cnpj';

        if (documentType === 'cpf') {
            if (validateCpf(document)) {
                setLastValidatedDocument(cleanDocument);
                toast.success("CPF válido!");
                return true;
            } else {
                setLastValidatedDocument(cleanDocument);
                toast.error("CPF inválido!");
                return false;
            }
        } else {
            if (cleanDocument.length !== 14) return false;

            setValidatingCnpj(true);
            try {
                const response = await openApi.post("/auth/validate-cnpj", {cnpj: cleanDocument});
                const data = response.data;
                setValidatingCnpj(false);

                if (data.valid && data.data.nome) {
                    if (data.data.tipo === 'cnpj') {
                        setFieldValue('companyName', data.data.nome);
                        toast.success("CNPJ válido! Nome da empresa preenchido automaticamente.");
                    } else {
                        toast.success("CPF válido!");
                    }
                    setLastValidatedDocument(cleanDocument);
                    return true;
                } else {
                    setLastValidatedDocument(cleanDocument);
                    toast.error("Documento inválido ou não encontrado na Receita Federal");
                    return false;
                }
            } catch (error) {
                setValidatingCnpj(false);
                console.error("Erro ao validar documento:", error);
                toast.error("Erro ao validar documento. Tente novamente.");
                setLastValidatedDocument(cleanDocument);
                return false;
            }
        }
    };

    const handleSignUp = async values => {
        if (!selectedPlan) {
            toast.error("Por favor, selecione um plano");
            return;
        }

        try {
            const signupData = {
                ...values,
                planId: selectedPlan.id
            };
            await openApi.post("/auth/signup", signupData);
            toast.success(i18n.t("signup.toasts.success"));
            history.push("/login");
        } catch (err) {
            toastError(err);
        }
    };

    let finalBackground;

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';

    if (currentMode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#f5f5f5";
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#303030";
        }
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{appName || "Multi100 - Cadastro"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"}/>
            </Helmet>

            <div
                className={classes.root}
                style={{
                    backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(")
                        ? finalBackground
                        : "none"
                }}
            >
                <CssBaseline/>
                
                <Dialog
                    open={true}
                    maxWidth="md"
                    fullWidth
                    disableEscapeKeyDown
                    onClose={() => {}}
                    PaperProps={{
                        className: classes.dialogPaper
                    }}
                    scroll="paper"
                >
                    <DialogContent className={classes.dialogContent}>
                        <div className={classes.container}>
                            {/* Header */}
                            <div className={classes.header}>
                                <Typography className={classes.title}>
                                    Transforme sua comunicação com nosso sistema
                                </Typography>
                                <Typography className={classes.subtitle}>
                                    Experimente gratuitamente por 3 dias todas as funcionalidades da nossa plataforma. Sem necessidade de cartão de crédito.
                                </Typography>
                            </div>

                            {/* Steps Indicator */}
                            <div className={classes.stepsContainer}>
                                <div className={classes.stepLine} style={{
                                    backgroundColor: currentStep >= 2 ? theme.palette.primary.main : theme.palette.divider
                                }}/>
                                <div style={{display: "flex", gap: "160px", position: "relative", zIndex: 1}}>
                                    <div className={classes.step}>
                                        <div className={`${classes.stepCircle} ${currentStep === 1 ? classes.stepCircleActive : currentStep > 1 ? classes.stepCircleCompleted : ""}`}>
                                            {currentStep > 1 ? <CheckIcon style={{fontSize: "18px"}}/> : "1"}
                                        </div>
                                        <Typography className={`${classes.stepLabel} ${currentStep === 1 ? classes.stepLabelActive : ""}`}>
                                            Selecione seu plano
                                        </Typography>
                                    </div>
                                    <div className={classes.step}>
                                        <div className={`${classes.stepCircle} ${currentStep === 2 ? classes.stepCircleActive : ""}`}>
                                            2
                                        </div>
                                        <Typography className={`${classes.stepLabel} ${currentStep === 2 ? classes.stepLabelActive : ""}`}>
                                            Crie sua conta
                                        </Typography>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className={`${classes.contentCard} ${classes.slideIn}`}>
                        {currentStep === 1 ? (
                            // Step 1: Plan Selection
                            <div>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                        <CircularProgress/>
                                    </Box>
                                ) : (
                                    <>
                                        <div className={classes.plansGrid}>
                                            {plans.map((plan) => {
                                                const features = getPlanFeatures(plan);
                                                return (
                                                    <Card
                                                        key={plan.id}
                                                        className={`${classes.planCard} ${selectedPlan?.id === plan.id ? classes.planCardSelected : ""}`}
                                                        onClick={() => handlePlanSelect(plan)}
                                                    >
                                                        <div className={classes.planHeader}>
                                                            <Typography className={classes.planName}>
                                                                {plan.name}
                                                            </Typography>
                                                            <Typography className={classes.planPrice}>
                                                                {formatCurrency(plan.amount)}
                                                            </Typography>
                                                            <Typography className={classes.planPricePeriod}>
                                                                /mês
                                                            </Typography>
                                                        </div>
                                                        <div className={classes.planFeatures}>
                                                            <div className={classes.featuresGrid}>
                                                                {features.map((feature, index) => (
                                                                    <div key={index} className={classes.featureItem}>
                                                                        <CheckCircleIcon className={classes.featureIcon}/>
                                                                        <span>{feature}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className={classes.planActions}>
                                                            <Button
                                                                fullWidth
                                                                variant={selectedPlan?.id === plan.id ? "contained" : "outlined"}
                                                                color="primary"
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePlanSelect(plan);
                                                                }}
                                                                style={{
                                                                    textTransform: "none",
                                                                    fontSize: "14px",
                                                                    padding: "8px 0"
                                                                }}
                                                            >
                                                                {selectedPlan?.id === plan.id ? "Selecionado" : "Selecionar"}
                                                            </Button>
                                                        </div>
                                                    </Card>
                                                );
                                            })}
                                        </div>
                                        {plans.length === 0 && !loading && (
                                            <Typography align="center" color="textSecondary">
                                                Nenhum plano disponível no momento.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            // Step 2: Company Data Form
                            <div>
                                <Link
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleBackToPlans();
                                    }}
                                    className={classes.backButton}
                                >
                                    <ArrowBackIcon style={{marginRight: "8px"}}/>
                                    Voltar para planos
                                </Link>

                                {selectedPlan && (
                                    <div className={classes.selectedPlanBar}>
                                        <Typography variant="body1" style={{fontWeight: 600}}>
                                            Plano selecionado: <strong>{selectedPlan.name}</strong>
                                        </Typography>
                                        <Typography variant="body1" style={{fontWeight: 600, color: theme.palette.primary.main}}>
                                            {formatCurrency(selectedPlan.amount)}/mês
                                        </Typography>
                                    </div>
                                )}

                                <Avatar className={classes.avatar}>
                                    <LockOutlinedIcon/>
                                </Avatar>
                                <Typography component="h1" variant="h6" align="center" gutterBottom style={{fontWeight: 600, marginBottom: "20px"}}>
                                    Cadastre sua empresa
                                </Typography>

                                <Formik
                                    initialValues={{...initialState, planId: selectedPlan?.id || ""}}
                                    enableReinitialize={true}
                                    validationSchema={UserSchema}
                                    onSubmit={(values, actions) => {
                                        setTimeout(() => {
                                            handleSignUp(values);
                                            actions.setSubmitting(false);
                                        }, 400);
                                    }}
                                >
                                    {({touched, errors, isSubmitting, setFieldValue}) => (
                                        <Form className={classes.form}>
                                            <Grid container spacing={2} style={{marginTop: "8px"}}>
                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="document"
                                                        label="CPF ou CNPJ"
                                                        name="document"
                                                        error={touched.document && Boolean(errors.document)}
                                                        helperText={touched.document && errors.document || "Digite CPF (11 dígitos) ou CNPJ (14 dígitos)"}
                                                        autoComplete="document"
                                                        inputProps={{
                                                            maxLength: 18,
                                                            style: {textTransform: 'none'}
                                                        }}
                                                        InputProps={{
                                                            endAdornment: validatingCnpj && (
                                                                <CircularProgress size={20}/>
                                                            ),
                                                        }}
                                                        autoFocus
                                                        onChange={(e) => {
                                                            let value = e.target.value;
                                                            const numbers = value.replace(/\D/g, '');

                                                            if (lastValidatedDocument && lastValidatedDocument !== numbers) {
                                                                setLastValidatedDocument("");
                                                            }

                                                            const formattedValue = formatDocument(value);
                                                            setFieldValue('document', formattedValue);

                                                            if (numbers.length === 11) {
                                                                validateDocument(formattedValue, setFieldValue);
                                                            } else if (numbers.length === 14) {
                                                                validateDocument(formattedValue, setFieldValue);
                                                            }
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="companyName"
                                                        label={i18n.t("signup.form.company")}
                                                        error={touched.companyName && Boolean(errors.companyName)}
                                                        helperText={touched.companyName && errors.companyName}
                                                        name="companyName"
                                                        autoComplete="companyName"
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        autoComplete="name"
                                                        name="name"
                                                        error={touched.name && Boolean(errors.name)}
                                                        helperText={touched.name && errors.name}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="name"
                                                        label={i18n.t("signup.form.name")}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="email"
                                                        label={i18n.t("signup.form.email")}
                                                        name="email"
                                                        error={touched.email && Boolean(errors.email)}
                                                        helperText={touched.email && errors.email}
                                                        autoComplete="email"
                                                        inputProps={{style: {textTransform: 'lowercase'}}}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        name="password"
                                                        error={touched.password && Boolean(errors.password)}
                                                        helperText={touched.password && errors.password || "Mínimo 5 caracteres"}
                                                        label={i18n.t("signup.form.password")}
                                                        type="password"
                                                        id="password"
                                                        autoComplete="current-password"
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Field
                                                        as={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        id="phone"
                                                        label={i18n.t("signup.form.phone")}
                                                        name="phone"
                                                        error={touched.phone && Boolean(errors.phone)}
                                                        helperText={touched.phone && errors.phone}
                                                        autoComplete="phone"
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Button
                                                type="submit"
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                className={classes.submit}
                                                disabled={validatingCnpj || isSubmitting}
                                            >
                                                {isSubmitting ? "Criando conta..." : selectedPlan?.trial ? `Iniciar teste de ${selectedPlan.trialDays} dias` : i18n.t("signup.buttons.submit")}
                                            </Button>

                                            <Grid container justify="center" style={{marginTop: "12px"}}>
                                                <Grid item>
                                                    <Link
                                                        href="#"
                                                        variant="body2"
                                                        component={RouterLink}
                                                        to="/login"
                                                        style={{fontSize: "13px"}}
                                                    >
                                                        {i18n.t("signup.buttons.login")}
                                                    </Link>
                                                </Grid>
                                            </Grid>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        )}
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </React.Fragment>
    );
};

export default SignUp;
